import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';
import { areaToM2, formatArea, m2ToInput } from '../utils/area';
import { money } from '../utils/money';
import { fmtDate } from '../utils/formatDate';

const LEASE_STATE = {
    SCHEDULED: { text: '예정', bg: '#e0e7ff', color: '#4338ca' },
    ACTIVE: { text: '임대 중', bg: '#d1fae5', color: '#059669' },
    EXPIRED: { text: '만료', bg: '#f3f4f6', color: '#6b7280' },
    TERMINATED: { text: '해지', bg: '#fee2e2', color: '#dc2626' },
};

const emptyDraft = () => ({ name: '', floor: '', area: '', memo: '' });

/**
 * 호실 관리 -- 빌려줄 수 있는 곳을 정하는 화면.
 *
 * 점검 구역과 나란히 있지만 다른 것이다. 미화는 3층을 한 번에 훑고, 그 층에
 * 임차인이 셋일 수 있다. 구역 관리와 화면을 나눠 둔 것은 둘을 같은 것으로
 * 여기지 않게 하려는 것이다.
 *
 * 이름은 '호실 관리' 다. 임대관리 프로그램들이 쓰는 말이라 다른 데서 넘어온
 * 사람이 그 이름으로 찾는다. 대신 층 전체를 빌려주는 건물이 망설이지 않도록,
 * 무엇을 더하는지를 먼저 고르게 한다 -- 호실인가, 층 전체인가.
 */
const UnitsPage = () => {
    const { id: facilityId } = useParams();
    const navigate = useNavigate();
    const { showAlert, showConfirm } = useModal();

    const [facility, setFacility] = useState(null);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(emptyDraft());
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    // 적는 단위. 저장은 늘 ㎡ 로 한다 -- 계약서에 적힌 숫자가 그것이고, 둘 다
    // 저장하면 언젠가 서로 맞지 않는 값이 들어간다.
    const [areaUnit, setAreaUnit] = useState('pyeong');
    // 호실인가, 층 전체인가. 이름 칸 하나만 두고 '층도 됩니다' 라고 적어 두면
    // 적어도 될지 망설이게 된다. 무엇을 더하는지를 먼저 고르게 한다.
    const [kind, setKind] = useState('room');
    /**
     * 펼쳐 둔 호실과, 호실별로 받아 둔 계약 이력.
     *
     * 목록을 열 때 한꺼번에 받지 않는다. 호실이 서른이면 서른 번을 묻게 되고,
     * 그중 사람이 실제로 여는 것은 한둘이다. 한 번 받은 것은 그대로 둔다.
     */
    const [openUnit, setOpenUnit] = useState(null);
    const [history, setHistory] = useState({});

    const toggleHistory = (unit) => {
        const closing = openUnit === unit.id;
        setOpenUnit(closing ? null : unit.id);
        if (closing || unit.id in history) return;

        setHistory((prev) => ({ ...prev, [unit.id]: null }));
        api
            .get('/leases', { params: { unitId: unit.id } })
            .then((res) =>
                setHistory((prev) => ({
                    ...prev,
                    // 서버는 곧 끝나는 것을 위에 둔다 -- 계약 목록에서 쓰는 순서다.
                    // 이력은 최근 것이 위여야 한다. 지난 일을 되짚을 때 먼저 묻는
                    // 것은 '바로 앞에 누가 있었나' 다.
                    [unit.id]: [...(res.data || [])].sort(
                        (a, b) => new Date(b.startDate) - new Date(a.startDate),
                    ),
                })),
            )
            .catch((err) => {
                console.error('Failed to load lease history', err);
                setHistory((prev) => ({ ...prev, [unit.id]: [] }));
            });
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [f, u] = await Promise.all([
                api.get(`/facilities/${facilityId}`),
                api.get('/units', { params: { facilityId } }),
            ]);
            setFacility(f.data);
            setUnits(u.data || []);
        } catch (err) {
            console.error('Failed to load units', err);
        } finally {
            setLoading(false);
        }
    }, [facilityId]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        const target = editing ?? draft;
        // 층 전체를 빌려주면 그 층이 임대 단위다. 이름 칸에 또 '3층' 을 적게
        // 하는 것은 같은 말을 두 번 시키는 일이라, 층을 이름으로 쓴다.
        const name =
            kind === 'floor'
                ? target.floor?.trim()
                : target.name?.trim() || target.floor?.trim();
        if (!name) {
            showAlert(
                '알림',
                kind === 'floor' ? '층을 적어 주세요.' : '호실 이름을 적어 주세요.',
                'warning',
            );
            return;
        }
        setSaving(true);
        try {
            const body = {
                name,
                floor: target.floor,
                areaM2: areaToM2(target.area, areaUnit),
                memo: target.memo,
            };
            if (editing) await api.patch(`/units/${editing.id}`, body);
            else await api.post('/units', { ...body, facilityId: Number(facilityId) });
            setDraft(emptyDraft());
            setEditing(null);
            await load();
        } catch (err) {
            showAlert(
                '오류',
                err.response?.data?.message || '저장하지 못했습니다.',
                'danger',
            );
        } finally {
            setSaving(false);
        }
    };

    const remove = async (unit) => {
        const ok = await showConfirm(
            '호실 삭제',
            `${unit.name}을 지울까요?`,
            'danger',
        );
        if (!ok) return;
        try {
            await api.delete(`/units/${unit.id}`);
            await load();
        } catch (err) {
            // 계약이 걸려 있으면 서버가 막는다. 왜 막혔는지를 그대로 보여준다.
            showAlert(
                '지울 수 없습니다',
                err.response?.data?.message || '지우지 못했습니다.',
                'warning',
            );
        }
    };

    /** 단위를 바꾸면 적어 둔 숫자도 함께 바꾼다. 숫자를 그대로 두면 25평이
        25㎡ 로 조용히 바뀐다. */
    const switchUnit = () => {
        const next = areaUnit === 'pyeong' ? 'm2' : 'pyeong';
        const current = (editing ?? draft).area;
        const converted =
            current === '' ? '' : m2ToInput(areaToM2(current, areaUnit), next);
        if (editing) setEditing({ ...editing, area: converted });
        else setDraft({ ...draft, area: converted });
        setAreaUnit(next);
    };

    const field = (key) => ({
        value: (editing ?? draft)[key] ?? '',
        onChange: (e) =>
            editing
                ? setEditing({ ...editing, [key]: e.target.value })
                : setDraft({ ...draft, [key]: e.target.value }),
    });

    return (
        <div className="pb-5">
            <div className="d-flex align-items-center gap-2 mb-3">
                <button className="btn border-0 p-0" onClick={() => navigate(-1)}>
                    <i className="bi bi-chevron-left fs-5"></i>
                </button>
                <div>
                    <h4 className="fw-bold mb-1">호실 관리</h4>
                    <p className="text-muted small mb-0">
                        {facility?.name ?? ''} · 빌려줄 수 있는 곳을 호실이나 층 단위로 정합니다. 점검 구역과는 별개입니다.
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-4 border p-3 mb-3">
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    <span className="fw-bold small">
                        {editing ? `${editing.name} 수정` : '추가할 것'}
                    </span>
                    {/* 층을 통째로 빌려주는 건물이 많다. 호실 번호를 지어내게
                        두면 계약서에 없는 이름이 생기고, 적어도 되는지 망설이게
                        된다. 처음부터 고르게 한다. */}
                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            className={`btn ${kind === 'room' ? 'btn-primary' : 'btn-white border'}`}
                            onClick={() => setKind('room')}
                        >
                            호실
                        </button>
                        <button
                            className={`btn ${kind === 'floor' ? 'btn-primary' : 'btn-white border'}`}
                            onClick={() => setKind('floor')}
                        >
                            층 전체
                        </button>
                    </div>
                    <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {kind === 'floor'
                            ? '한 층을 한 임차인이 통째로 씁니다. 「3층」이 그대로 호실이 됩니다.'
                            : '301호처럼 층 안에 여러 곳이 있을 때.'}
                    </span>
                </div>
                <div className="row g-2">
                    {kind === 'room' && (
                        <div className="col-6 col-md-3">
                            <input className="form-control" placeholder="301호" {...field('name')} />
                        </div>
                    )}
                    <div className={kind === 'floor' ? 'col-6 col-md-5' : 'col-6 col-md-2'}>
                        <input
                            className="form-control"
                            placeholder={kind === 'floor' ? '3층 · B1 · 옥상' : '3층'}
                            {...field('floor')}
                        />
                    </div>
                    <div className="col-6 col-md-2">
                        <div className="input-group">
                            <input
                                className="form-control"
                                type="number"
                                step="0.01"
                                placeholder="전용면적"
                                {...field('area')}
                            />
                            {/* 아는 단위로 적게 둔다. 계약서는 ㎡, 사람은 평으로
                                말하는데 매번 손으로 나누게 할 이유가 없다. */}
                            <button
                                className="btn btn-white border text-nowrap px-2"
                                onClick={switchUnit}
                                title="단위 바꾸기"
                            >
                                {areaUnit === 'pyeong' ? '평' : '㎡'}
                            </button>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <input className="form-control" placeholder="메모" {...field('memo')} />
                    </div>
                    <div className="col-12 col-md-2 d-flex gap-2">
                        <button
                            className="btn btn-primary flex-grow-1"
                            onClick={save}
                            disabled={saving}
                        >
                            {editing ? '수정' : '추가'}
                        </button>
                        {editing && (
                            <button
                                className="btn btn-white border"
                                onClick={() => setEditing(null)}
                            >
                                취소
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : units.length === 0 ? (
                <div className="bg-white rounded-4 border text-center p-5">
                    <i className="bi bi-door-closed display-6 d-block mb-3 text-muted opacity-50"></i>
                    <h6 className="fw-bold mb-1">호실이 없습니다</h6>
                    <p className="text-muted small mb-0">위에서 첫 호실을 더해 주세요.</p>
                </div>
            ) : (
                <div className="bg-white rounded-4 border">
                    {units.map((u) => (
                        <div key={u.id} className="border-bottom">
                        <div className="d-flex align-items-center gap-2 p-3">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-bold" style={{ fontSize: '0.95rem' }}>
                                    {u.name}
                                    {/* 이름과 층이 같으면 층을 통째로 빌려주는 곳이다.
                                        층을 한 번 더 적는 대신 그렇다고 말해 준다. */}
                                    {u.floor === u.name && (
                                        <span className="badge bg-light text-secondary border fw-normal ms-2" style={{ fontSize: '0.68rem' }}>
                                            층 전체
                                        </span>
                                    )}
                                    {u.floor && u.floor !== u.name && (
                                        <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.78rem' }}>
                                            {u.floor}
                                        </span>
                                    )}
                                    {u.areaM2 && (
                                        <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.78rem' }}>
                                            {formatArea(u.areaM2)}
                                        </span>
                                    )}
                                </div>
                                {/* 지금 누가 쓰는지가 이 목록에서 가장 먼저 보고 싶은 값이다. */}
                                <div style={{ fontSize: '0.78rem' }}>
                                    {u.currentLease ? (
                                        <span className="text-dark">
                                            <i className="bi bi-person-fill text-primary me-1"></i>
                                            {u.currentLease.tenantName}
                                        </span>
                                    ) : (
                                        <span className="text-muted">공실</span>
                                    )}
                                    {u.memo && <span className="text-muted ms-2">· {u.memo}</span>}
                                </div>
                            </div>
                            {/* 지난 계약은 접어 둔다. 공실이든 아니든 '전에 누가
                                있었나' 는 늘 물을 수 있어야 한다. */}
                            <button
                                className="btn btn-sm border-0 p-0 text-secondary"
                                onClick={() => toggleHistory(u)}
                            >
                                <i className={`bi bi-clock-history me-1`}></i>
                                <span className="d-none d-md-inline">계약 이력</span>
                                <i className={`bi bi-chevron-${openUnit === u.id ? 'up' : 'down'} ms-1`} style={{ fontSize: '0.7rem' }}></i>
                            </button>
                            <button
                                className="btn btn-sm border-0 p-0 text-secondary ms-3"
                                onClick={() => {
                                    setKind(u.floor === u.name ? 'floor' : 'room');
                                    setEditing({ ...u, area: m2ToInput(u.areaM2, areaUnit) });
                                }}
                            >
                                수정
                            </button>
                            <button
                                className="btn btn-sm border-0 p-0 text-danger ms-2"
                                onClick={() => remove(u)}
                            >
                                삭제
                            </button>
                        </div>

                        {openUnit === u.id && (
                            <div className="px-3 pb-3">
                                {history[u.id] === null || history[u.id] === undefined ? (
                                    <div className="text-muted small">불러오는 중…</div>
                                ) : history[u.id].length === 0 ? (
                                    <div className="text-muted small">
                                        이 호실에 등록된 계약이 아직 없습니다.
                                    </div>
                                ) : (
                                    <div className="bg-light rounded-3 p-2">
                                        {history[u.id].map((l) => {
                                            const st = LEASE_STATE[l.status] || LEASE_STATE.EXPIRED;
                                            // 한 계약서가 여러 호실을 덮는 일이 흔하다.
                                            // 301+302 를 함께 쓰던 임차인이 301 만
                                            // 남기고 줄인 것이 이력에서 보여야 한다.
                                            const others = (l.units || []).filter((x) => x.id !== u.id);
                                            return (
                                                <button
                                                    key={l.id}
                                                    className="btn w-100 text-start d-flex align-items-start gap-2 p-2"
                                                    onClick={() => navigate(`/leases/${l.id}`)}
                                                >
                                                    <span
                                                        className="badge fw-normal mt-1"
                                                        style={{ background: st.bg, color: st.color, fontSize: '0.68rem' }}
                                                    >
                                                        {st.text}
                                                    </span>
                                                    <span style={{ minWidth: 0 }}>
                                                        <span className="fw-bold d-block text-truncate" style={{ fontSize: '0.85rem' }}>
                                                            {l.tenant?.name || '임차인 미상'}
                                                        </span>
                                                        <span className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                                            {fmtDate(l.startDate)} – {fmtDate(l.endDate)}
                                                            {money(l.monthlyRent) && ` · 월 ${money(l.monthlyRent)}`}
                                                            {money(l.deposit) && ` · 보증금 ${money(l.deposit)}`}
                                                        </span>
                                                        {others.length > 0 && (
                                                            <span className="text-muted d-block" style={{ fontSize: '0.72rem' }}>
                                                                <i className="bi bi-plus-square me-1"></i>
                                                                {others.map((x) => x.name).join(', ')} 함께 계약
                                                            </span>
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UnitsPage;
